import type { BoardPlacement } from "./Board.types";
import { getCellEntries, type BoardCells } from "./boardCells";
import { stepThrough } from "./boardCollapse";
import type { BoardKeyboardDirection } from "./boardDropTargets";
import { locateBoardEntry } from "./boardMove";
import type { BoardNavigationKey } from "./boardNavigationKeys";

/** A card the board's Tab stop can sit on: its key and where it is. */
export interface BoardCardSpot {
    placementKey: string;
    placement: BoardPlacement;
    index: number;
}

/** The columns and lanes keyboard focus steps through: the expanded ones. */
export interface BoardExpandedIds {
    columnIds: readonly string[];
    /** `[null]` without lanes. */
    laneIds: ReadonlyArray<string | null>;
}

/** An arrow with nowhere to go; `hasColumn` when columns that way hold no card. */
export interface BoardNavigationEdge {
    /** `first` and `last`: Home, End or a page key at that end of the cell. */
    direction: BoardKeyboardDirection | "first" | "last";
    hasColumn: boolean;
}

export type BoardNavigationResult =
    | { kind: "card"; spot: BoardCardSpot }
    | { kind: "edge"; edge: BoardNavigationEdge }
    | { kind: "none" };

const NONE: BoardNavigationResult = { kind: "none" };

const getSpotAt = <TItem>(
    cells: BoardCells<TItem>,
    placement: BoardPlacement,
    index: number,
): BoardCardSpot | null => {
    const entries = getCellEntries(cells, placement);
    const clamped = Math.min(Math.max(index, 0), entries.length - 1);
    const entry = entries[clamped];
    return entry === undefined
        ? null
        : { placementKey: entry.placementKey, placement, index: clamped };
};

/** Where the card with `placementKey` is now, or null once it's gone. */
export const locateCardSpot = <TItem>(
    cells: BoardCells<TItem>,
    placementKey: string,
): BoardCardSpot | null => {
    const location = locateBoardEntry(cells, placementKey);
    return location === null
        ? null
        : {
              placementKey,
              placement: location.entry.placement,
              index: location.index,
          };
};

export const isExpanded = (
    { columnIds, laneIds }: BoardExpandedIds,
    { columnId, laneId }: BoardPlacement,
) => columnIds.includes(columnId) && laneIds.includes(laneId);

interface ResolveActiveCardArgs<TItem> {
    cells: BoardCells<TItem>;
    expanded: BoardExpandedIds;
    /** The card last focused or navigated to, as it was then. */
    stored: BoardCardSpot | null;
}

/**
 * The card holding the board's Tab stop: the stored one while it shows;
 * once it's gone (deleted, filtered out, moved, collapsed) the card now at
 * its index in its cell, else the first card in reading order (lanes top to
 * bottom, columns left to right).
 */
export const resolveActiveCard = <TItem>({
    cells,
    expanded,
    stored,
}: ResolveActiveCardArgs<TItem>): BoardCardSpot | null => {
    const current =
        stored === null ? null : locateCardSpot(cells, stored.placementKey);
    if (current !== null && isExpanded(expanded, current.placement)) {
        return current;
    }
    const nearest =
        stored !== null && isExpanded(expanded, stored.placement)
            ? getSpotAt(cells, stored.placement, stored.index)
            : null;
    if (nearest !== null) {
        return nearest;
    }
    for (const laneId of expanded.laneIds) {
        for (const columnId of expanded.columnIds) {
            const first = getSpotAt(cells, { columnId, laneId }, 0);
            if (first !== null) {
                return first;
            }
        }
    }
    return null;
};

interface NavigationArgs<TItem> {
    cells: BoardCells<TItem>;
    expanded: BoardExpandedIds;
    from: BoardCardSpot;
    key: BoardNavigationKey;
    /** Cards PageUp and PageDown jump. */
    pageSize: number;
}

const toCard = (
    spot: BoardCardSpot | null,
    from: BoardCardSpot,
): BoardNavigationResult =>
    spot === null || spot.placementKey === from.placementKey
        ? NONE
        : { kind: "card", spot };

/** Home, End and the page keys stay in the cell; already at that end is an edge. */
const toCardInCell = (
    spot: BoardCardSpot | null,
    from: BoardCardSpot,
    end: "first" | "last",
): BoardNavigationResult =>
    spot === null || spot.placementKey === from.placementKey
        ? { kind: "edge", edge: { direction: end, hasColumn: false } }
        : { kind: "card", spot };

interface StepArgs<TItem, TDirection> {
    cells: BoardCells<TItem>;
    expanded: BoardExpandedIds;
    from: BoardCardSpot;
    direction: TDirection;
}

const hasCards = <TItem>(cells: BoardCells<TItem>, placement: BoardPlacement) =>
    getCellEntries(cells, placement).length > 0;

/** Up and down: through the cell, then into the next lane with a card here. */
const stepVertically = <TItem>({
    cells,
    expanded,
    from,
    direction,
}: StepArgs<TItem, "up" | "down">): BoardNavigationResult => {
    const { placement, index } = from;
    const step = direction === "down" ? 1 : -1;
    const nextIndex = index + step;
    if (nextIndex >= 0 && nextIndex < getCellEntries(cells, placement).length) {
        return toCard(getSpotAt(cells, placement, nextIndex), from);
    }
    const lane = stepThrough({
        ids: expanded.laneIds,
        id: placement.laneId,
        step,
        accepts: (laneId) =>
            hasCards(cells, { columnId: placement.columnId, laneId }),
    });
    if (lane === undefined) {
        return { kind: "edge", edge: { direction, hasColumn: false } };
    }
    return toCard(
        getSpotAt(
            cells,
            { columnId: placement.columnId, laneId: lane.id },
            step > 0 ? 0 : Infinity,
        ),
        from,
    );
};

/** Left and right: the next column with a card in this lane, index clamped. */
const stepAcrossColumns = <TItem>({
    cells,
    expanded,
    from,
    direction,
}: StepArgs<TItem, "left" | "right">): BoardNavigationResult => {
    const { placement, index } = from;
    const step: 1 | -1 = direction === "right" ? 1 : -1;
    const steps = { ids: expanded.columnIds, id: placement.columnId, step };
    const column = stepThrough({
        ...steps,
        accepts: (columnId) =>
            hasCards(cells, { columnId, laneId: placement.laneId }),
    });
    if (column === undefined) {
        const hasColumn = stepThrough(steps) !== undefined;
        return { kind: "edge", edge: { direction, hasColumn } };
    }
    return toCard(
        getSpotAt(
            cells,
            { columnId: column.id, laneId: placement.laneId },
            index,
        ),
        from,
    );
};

/**
 * Where a key moves focus from `from`, on the board model. Up and down step
 * through the cell, then into the nearest lane that way with a card in this
 * column. Left and right go to the nearest column that way with a card in
 * this lane, at the same index, clamped. Home, End and the page keys stay in
 * the cell. Nothing wraps.
 */
export const getNavigationTarget = <TItem>({
    cells,
    expanded,
    from,
    key,
    pageSize,
}: NavigationArgs<TItem>): BoardNavigationResult => {
    const { placement, index } = from;
    const lastIndex = getCellEntries(cells, placement).length - 1;

    switch (key) {
        case "home":
            return toCardInCell(getSpotAt(cells, placement, 0), from, "first");
        case "end":
            return toCardInCell(
                getSpotAt(cells, placement, lastIndex),
                from,
                "last",
            );
        case "pageUp":
            return toCardInCell(
                getSpotAt(cells, placement, index - pageSize),
                from,
                "first",
            );
        case "pageDown":
            return toCardInCell(
                getSpotAt(cells, placement, index + pageSize),
                from,
                "last",
            );
        case "up":
        case "down":
            return stepVertically({ cells, expanded, from, direction: key });
        case "left":
        case "right":
            return stepAcrossColumns({ cells, expanded, from, direction: key });
    }
};
